import * as React from 'react';
import { createFileRoute, useNavigate, useRouterState } from '@tanstack/react-router';
import type { FileRouteTypes } from '@tanstack/react-router';
import { useAuth } from '../../../../contexts/AuthContext';
import { Unauthorized } from '../../../../components/Unauthorized';
import { Chart, PatientDoc, FinalUpdate, NeedsReview, Roles, CardStatus, ByLogo, APPLICATION_STATUS } from '@usacsmideo/types';
import useChart from '../../../../hooks/useChart';
import { useUpdateChartSection } from '../../../../hooks/useUpdateChartSection';
import { notifyChartSaveError } from '../../../../utilities/chartSaveError';
import usePatientDoc from '../../../../hooks/usePatientDoc';
import { useUpdateUserDoc } from '../../../../hooks/useUpdateUserDoc';

import { Heading } from '../../../../components/Heading';
import { FormSelect } from '../../../../components/forms/FormSelect';
import { CardFront } from '../../../../components/card/CardFront';
import { CardBack } from '../../../../components/card/CardBack';

import { sendAutomatedEmail } from '../../../../data/sendAutomatedEmail';
import { setCompletedPage } from '../../../../data/navigation/utilities/setCompletedPage';
import { normalizePortalPath } from '../../../../utilities/normalizePortalPath';
import { updateNavObjAndGetNextScreen } from '../../../../data/navigation/updateNavObjAndGetNextScreen';
import { useIsTerminalScreen } from '../../../../hooks/useIsTerminalScreen';
import TerminalScreenMessage from '../../../../components/navigation/TerminalScreenMessage';
import { classNames } from '../../../../style/classNames';

import * as Sentry from '@sentry/browser';

export const Route = createFileRoute('/_authenticated/$patientId/card/cardReview')({
  component: CardReviewPage,
});

function CardReviewPage() {
  const { patientId } = Route.useParams();
  const { currentUser } = useAuth();
  const chartQuery = useChart(patientId);
  const chart = chartQuery.data as Chart;
  const { mutate: mutateChartDoc } = useUpdateChartSection();
  const patientDocQuery = usePatientDoc(patientId);
  const patientInfo = patientDocQuery.data as PatientDoc;
  const { mutate: mutatePatientDoc } = useUpdateUserDoc();
  const navigate = useNavigate();
  const routerState = useRouterState();
  const pathname = routerState.location.pathname;
  const isTerminal = useIsTerminalScreen(pathname, patientInfo?.navObj);

  const applicationStatus = patientInfo?.applicationStatus;

  // — Preview mode state —
  const [currentError, setCurrentError] = React.useState<string>('select');

  const potentialErrors = [
    { name: 'Select an error', value: 'select' },
    {
      name: 'My name, date of birth, and/or address is incorrect',
      value: 'patientInfo',
    },
    { name: 'My organ donor status is incorrect', value: 'organDonor' },
    {
      name: "My healthcare agent's information is incorrect",
      value: 'healthcareAgent',
    },
    {
      name: 'My medical order document information is incorrect',
      value: 'medicalOrder',
    },
  ];

  const handleErrorSelection = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setCurrentError(e.target.value);
    switch (e.target.value) {
      case 'patientInfo':
        navigate({ to: `/${patientId}/info/patientInfo` });
        break;
      case 'organDonor':
        navigate({ to: `/${patientId}/info/check` });
        break;
      case 'healthcareAgent':
        navigate({ to: `/${patientId}/info/hcaInfo` });
        break;
      case 'medicalOrder':
        navigate({ to: `/${patientId}/upload/files` });
        break;
      default:
        break;
    }
  };

  // — Preview mode: submit application —
  const handleSubmitApplication = async () => {
    // KICK OFF THE AUTOMATED "CARD IN REVIEW" EMAIL
    sendAutomatedEmail({
      type: 'cardInReview',
      patientId: patientId,
      email: patientInfo?.contactEmail || patientInfo?.accountEmail,
      name: `${chart?.PATIENT_INFO_1?.firstName} ${chart?.PATIENT_INFO_1?.lastName}`,
    });
    // MARK FINAL SIGNATURES PAGE AS COMPLETED
    await setCompletedPage({
      name: 'finalSignatures',
      value: true,
      patientInfo: patientInfo,
    })
      .then(async () => {
        if (!chart.OTHER_DOCUMENTS) {
          mutateChartDoc(
            {
              patientId: patientId,
              docData: {
                OTHER_DOCUMENTS: {},
              },
            },
            {
              onSuccess: () => {
                // alert("Successfully updated document");
              },
              onError: (error) => {
                Sentry.captureException(error);
                alert(`Error updating document: ${error.message}`); // TELL THE USER
                console.error(error); // GET THE STACKTRACE
              },
            },
          );
        }
      })
      .then(async () => {
        // UPDATE FILTER OPTIONS TO SHOW FULL CODE
        let filterOptionsClone = structuredClone(patientInfo.filterOptions);
        if (!filterOptionsClone) {
          filterOptionsClone = {
            code: chart.SELECTED_TREATMENT_OPTION?.code ? chart.SELECTED_TREATMENT_OPTION.code : 'fullCode',
            createdOn: Date.now(),
            isArchived: false,
          };
        } else {
          filterOptionsClone.code = chart.SELECTED_TREATMENT_OPTION?.code
            ? chart.SELECTED_TREATMENT_OPTION.code
            : 'fullCode';
          // createdOn is the patient creation timestamp — set once, never on card review.
          // Preserve any existing value; only backfill it if genuinely absent (CU-86ahfzf4k).
          if (filterOptionsClone.createdOn == null) {
            filterOptionsClone.createdOn = Date.now();
          }
          filterOptionsClone.isArchived = false;
        }

        // UPDATE DISPLAY NAME TO SHOW PATIENT NAME IN WEB CONSOLE
        const displayName = `${chart.PATIENT_INFO_1.firstName} ${chart.PATIENT_INFO_1.middleInitial}. ${chart.PATIENT_INFO_1.lastName}`;

        const dataObj = {
          displayName,
          filterOptions: filterOptionsClone,
          applicationStatus: APPLICATION_STATUS.SUBMITTED,
          byLogo: {
            active: true,
            createdAt: {
              seconds: Date.now() / 1000,
              nanoseconds: 0,
            },
            createdBy: 'system',
            defaultLogo: true,
            downloadUri: `${import.meta.env.VITE_PUBLIC_STORAGE_URL}/logos/defaultByLogo.png`,
            id: 'defaultByLogo.png',
            storagePath: 'logos',
          } as ByLogo,
          secondaryLogo: {
            active: true,
            createdAt: {
              seconds: Date.now() / 1000,
              nanoseconds: 0,
            },
            createdBy: 'system',
            defaultLogo: true,
            downloadUri: `${import.meta.env.VITE_PUBLIC_STORAGE_URL}/logos/defaultSecondaryLogo.png`,
            id: 'defaultSecondaryLogo.png',
            storagePath: 'logos',
          } as ByLogo,
          roles: {
            isPatient: true,
            orgAdmin: false,
            OrgQC: false,
            siteAdmin: false,
            siteQC: false,
            acpe: false,
            provider: false,
          } as Roles,
          needsReview: {
            flagged: false,
            flaggedByName: '',
            flaggedByUID: '',
            flaggedReason: '',
            flaggedTime: 0,
            uid: '',
            unflaggedByUID: '',
          } as NeedsReview,
          isArchived: false,
          cardStatus: {
            cardPrinted: {
              description: 'Text verified',
              isComplete: false,
              sortOrder: 5,
            },
            codeCorrect: {
              description: 'Confirmed QR code links to correct video.',
              isComplete: false,
              sortOrder: 6,
            },
            imageCorrect: {
              description: 'Patient photo Verified',
              isComplete: false,
              sortOrder: 2,
            },
            releaseApproved: {
              description: 'Card released',
              isComplete: false,
              sortOrder: 7,
            },
            textVerified: {
              description: 'Card text verified',
              isComplete: false,
              sortOrder: 1,
            },
            videoProcessed: {
              description: 'Video processed',
              isComplete: false,
              sortOrder: 4,
            },
            videoVerified: {
              description: 'Video verified',
              isComplete: false,
              sortOrder: 3,
            },
          } as CardStatus,
        };
        mutatePatientDoc(
          {
            patientId: patientId,
            data: dataObj,
          } as FinalUpdate,
          {
            onSuccess: () => {
              // alert('Successfully updated document');
            },
            onError: (error) => {
              Sentry.captureException(error);
              alert(`Error updating document: ${error.message}`); // TELL THE USER
              console.error(error); // GET THE STACKTRACE
            },
          },
        );
      })
      .then(() => {
        mutateChartDoc(
          {
            patientId: patientId,
            docData: {
              SELECTED_TREATMENT_OPTION: {
                code: 'fullCode',
                description: 'Full Code - All Treatment & No Limitations in Care',
              },
            },
            applicationStatus: APPLICATION_STATUS.SUBMITTED,
          },
          {
            onSuccess: () => {
              // alert("Successfully updated document");
            },
            onError: () => {
              notifyChartSaveError();
            },
          },
        );
      })
      .then(async () => {
        window.localStorage.removeItem('emailForSignIn');
      });
  };

  // — Final mode: forward navigation —
  const handleNextButtonClick = async () => {
    const res = await updateNavObjAndGetNextScreen({
      patientId,
      currentPage: pathname,
      patientInfo: patientInfo,
      chart: chart,
    });

    if ('incompleteMessage' in res) {
      alert(`Incomplete Information: ${res.incompleteMessage}`);
      return;
    }
    if ('errorMessage' in res) {
      alert(`Error: ${res.errorMessage}`);
      return;
    }
    if ('complete' in res) {
      mutatePatientDoc(
        { patientId: patientId, navObj: res.updatedNavObj },
        {
          onSuccess: () => {
            navigate({ to: '/$patientId', params: { patientId } });
          },
          onError: (error: Error) => {
            Sentry.captureException(
              new Error(
                `Error updating patient navigation object for patient ${patientId}: ${error.message}`,
              ),
            );
            console.error('Error updating patient navigation object:', error);
            alert(
              'There was an error updating your information. Please try again later or contact support if this issue persists.',
            );
          },
        },
      );
      return;
    }

    mutatePatientDoc(
      {
        patientId: patientId,
        navObj: res.updatedNavObj,
      },
      {
        onSuccess: () => {
          navigate({ to: normalizePortalPath(res.nextScreen.portalPath) as FileRouteTypes['to'] });
        },
        onError: (error) => {
          Sentry.captureException(
            new Error(
              `Error updating patient navigation object for patient ${patientId}: ${error.message}`,
            ),
          );
          console.error('Error updating patient navigation object:', error);
          alert(
            'There was an error updating your information. Please try again later or contact support if this issue persists.',
          );
        },
      },
    );
  };

  // — Loading / auth guards —
  if (chartQuery.isLoading || !chart) {
    return <div>Loading patient information...</div>;
  }
  if (!currentUser) {
    return <div>Loading current user</div>;
  }
  if (currentUser?.uid !== patientId) {
    return <Unauthorized />;
  }

  // ── Mode 2: Pending (submitted / printable / printed) ──
  if (
    applicationStatus === 'submitted' ||
    applicationStatus === 'printable' ||
    applicationStatus === 'printed'
  ) {
    return (
      <>
        <div id="card-pending-page" className="flex w-full flex-col items-center">
          <Heading id="card-pending" textContent="Card Pending" />
          <div className="mt-8 flex max-w-lg flex-col items-center text-center">
            <p className="text-2xl font-bold">Your application is currently in review.</p>
            <p className="mt-4 text-xl">
              You will receive an email notification once the review is complete.
            </p>
            <p className="mt-8 text-lg">
              If you have any questions or concerns, email us at{' '}
              <button
                className="text-blue-500 underline"
                onClick={() => (window.location.href = 'mailto:info@mideohealth.com')}
              >
                info@mideohealth.com
              </button>
            </p>
          </div>
        </div>
      </>
    );
  }

  // ── Mode 3: Final (closed / released) ──
  if (applicationStatus === 'closed' || applicationStatus === 'released') {
    return (
      <>
        <div id="card-final-page" className="flex w-full flex-col">
          <div id="card-final-page-header" className="flex flex-col">
            <Heading id="card-final" textContent="Your MIDEO Card" />
            <p className="text-center text-2xl @sm:text-sm">
              Below is your completed MIDEO card (front/back).
            </p>
          </div>
          <div id="card-final-container" className="flex flex-col items-center justify-center">
            <CardFront chart={chart} patientId={patientId} mode="final" />
            <CardBack chart={chart} patientId={patientId} mode="final" />
          </div>
          <div className="flex flex-col items-center">
            <p className="text-center text-2xl">
              If you have any questions or concerns, email us at{' '}
              <button
                className="text-blue-500 underline"
                onClick={() => (window.location.href = 'mailto:info@mideohealth.com')}
              >
                info@mideohealth.com
              </button>
            </p>
          </div>
          {isTerminal ? (
            <TerminalScreenMessage onReturnToOverview={handleNextButtonClick} />
          ) : (
            <button onClick={handleNextButtonClick} className={classNames.nextButton}>
              Next
            </button>
          )}
        </div>
      </>
    );
  }

  // ── Mode 1: Preview (open — default) ──
  return (
    <>
      <div id="card-preview-page" className="flex w-full flex-col">
        <div id="card-preview-page-header" className="flex flex-col">
          <Heading id="card-preview" textContent="Card Preview" />
          <p className="text-center text-2xl @sm:text-sm">
            Below is a preview of your upcoming card (front/back).Please review for accuracy.
          </p>
        </div>
        <div id="card-preview-container" className="flex flex-col items-center justify-center">
          <CardFront chart={chart} patientId={patientId} mode="preview" />
          <CardBack chart={chart} patientId={patientId} mode="preview" />
        </div>
        <div id="verification-area" className="flex flex-col items-center">
          <p className="text-center text-2xl italic">
            If you see any errors in the samples above, making a selection in the box below will take you to the page
            where you can fix the error. Make the changes and click the submit button to save them. Then use the
            browser&apos;s back button to return here.
          </p>
          <FormSelect
            label=""
            id="correct-errors"
            name="correct-errors"
            value={currentError}
            required={false}
            onBlur={() => {
              return;
            }}
            onChange={(e) => handleErrorSelection(e)}
            options={potentialErrors}
          />
        </div>
        {(chart?.OTHER_DOCUMENTS?.livingWillExists && chart?.OTHER_DOCUMENTS?.livingWillURI?.length === 0) ||
        (chart?.OTHER_DOCUMENTS?.POLSTExists && chart?.OTHER_DOCUMENTS?.POLSTURI?.length === 0) ||
        (chart?.OTHER_DOCUMENTS?.powerOfAttorneyExists &&
          chart?.OTHER_DOCUMENTS?.powerOfAttorneyURI?.length === 0) ? (
          <div className="mt-6 flex flex-col items-center">
            <p className="text-center text-2xl font-bold text-red-500 underline">ATTENTION:</p>
            <p className="text-center text-2xl font-bold text-red-500">YOUR APPLICATION IS MISSING DOCUMENTS</p>
            <p className="mt-4 text-center text-xl">
              You stated you have a{' '}
              {chart?.OTHER_DOCUMENTS?.livingWillExists
                ? 'living will'
                : chart?.OTHER_DOCUMENTS?.POLSTExists
                  ? 'POLST document'
                  : 'power of attorney document'}{' '}
              but have not yet uploaded it. You can{' '}
              <button
                className="text-blue-500 underline"
                onClick={() => {
                  navigate({ to: `/${patientId}/upload/files` });
                }}
              >
                CLICK HERE
              </button>{' '}
              to upload it now. Or if you plan to email your documents to{' '}
              <button
                className="text-blue-500 underline"
                onClick={() => (window.location.href = 'mailto:info@mideohealth.com')}
              >
                info@mideohealth.com
              </button>{' '}
              at a later time, you can click the &apos;Submit Application&apos; button below.
            </p>
            <div className="mt-4">
              <button
                id={'nav-forward'}
                className="m-2 place-self-center rounded-md bg-red-900 px-4 py-2 text-white hover:bg-red-950 disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-500 disabled:shadow-none sm:px-8 sm:py-3"
                type={'button'}
                onClick={handleSubmitApplication}
              >
                Submit Application
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center">
            <p className="text-center text-2xl font-bold text-green-500">YOUR APPLICATION IS READY FOR SUBMISSION</p>
            <p className="text-center text-2xl italic">Please click the button below to submit your application.</p>
            <button
              id={'nav-forward'}
              className="m-2 place-self-center rounded-md bg-red-900 px-4 py-2 text-white hover:bg-red-950 disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-500 disabled:shadow-none sm:px-8 sm:py-3"
              type={'button'}
              onClick={handleSubmitApplication}
            >
              Submit Application
            </button>
          </div>
        )}
      </div>
    </>
  );
}
